import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Quiz = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/quiz`, {
        params: { limit: 10 }
      });
      setQuestions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async (answer) => {
    if (answered) return;
    
    setSelectedAnswer(answer);
    setAnswered(true);

    try {
      const response = await axios.post(
        `${API}/quiz/submit`,
        {
          user_id: 'temp',
          question_id: questions[currentIndex].id,
          answer
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.correct) {
        setScore(score + 1);
        toast.success('Bonne réponse! 🎉');
      } else {
        toast.error(`Mauvaise réponse. La bonne réponse était: ${response.data.correct_answer}`);
      }
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setAnswered(false);
    } else {
      setShowResult(true);
    }
  };

  const restartQuiz = () => {
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswered(false);
    fetchQuestions();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">Chargement du quiz...</p>
      </div>
    );
  }

  if (showResult) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-md w-full border-2 border-stone-200 text-center"
        >
          <h2 className="font-playfair text-4xl font-bold text-primary mb-4">
            Quiz Terminé!
          </h2>
          <p className="font-nunito text-6xl font-bold text-accent mb-6">
            {score}/{questions.length}
          </p>
          <p className="font-nunito text-xl text-charcoal mb-8">
            Vous avez obtenu {percentage}%!
          </p>
          <div className="space-y-3">
            <button
              onClick={restartQuiz}
              data-testid="restart-quiz-btn"
              className="w-full tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] active:shadow-none"
            >
              Recommencer
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-result"
              className="w-full tile-button bg-secondary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#004d40] active:shadow-none"
            >
              Retour au tableau de bord
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-sand">
      <nav className="bg-white border-b-2 border-stone-200 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard"
              className="tile-button bg-stone-200 text-charcoal p-2 rounded-lg hover:bg-stone-300"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-playfair text-3xl font-bold text-primary" data-testid="quiz-title">
              Quiz
            </h1>
          </div>
          <div className="font-nunito font-bold text-lg text-charcoal">
            {currentIndex + 1}/{questions.length}
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-2 border-stone-200"
        >
          <div className="mb-8">
            <div className="w-full bg-stone-200 rounded-full h-3 mb-6">
              <div
                className="bg-accent h-3 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
            <h2 className="font-nunito text-2xl font-bold text-charcoal mb-2" data-testid="question-text">
              {currentQuestion.question}
            </h2>
            <span className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-nunito font-semibold">
              {currentQuestion.category}
            </span>
          </div>

          <div className="space-y-4 mb-8">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                disabled={answered}
                data-testid={`option-${index}`}
                className={`w-full tile-button font-nunito font-semibold py-4 px-6 rounded-xl text-left transition-all ${
                  answered && option === currentQuestion.correct_answer
                    ? 'bg-secondary text-white shadow-[0_4px_0_#004d40]'
                    : answered && option === selectedAnswer && option !== currentQuestion.correct_answer
                    ? 'bg-destructive text-white shadow-[0_4px_0_#dc2626]'
                    : answered
                    ? 'bg-stone-200 text-charcoal/50'
                    : 'bg-white border-2 border-stone-300 text-charcoal hover:border-primary hover:shadow-tile'
                } disabled:cursor-not-allowed`}
              >
                <div className="flex items-center justify-between">
                  <span>{option}</span>
                  {answered && option === currentQuestion.correct_answer && (
                    <Check className="w-6 h-6" />
                  )}
                  {answered && option === selectedAnswer && option !== currentQuestion.correct_answer && (
                    <X className="w-6 h-6" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {answered && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={nextQuestion}
              data-testid="next-question-btn"
              className="w-full tile-button bg-primary text-white font-nunito font-bold py-3 px-6 rounded-xl shadow-[0_4px_0_#9a360b] active:shadow-none"
            >
              {currentIndex < questions.length - 1 ? 'Question suivante' : 'Voir les résultats'}
            </motion.button>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Quiz;